import type { AxiosInstance } from 'axios';

type Router = {
  clearHistory: () => void;
  on: (
    type: 'finish',
    callback: (event: { detail: { visit: { method: string; completed: boolean } } }) => void
  ) => unknown;
};

export const isWrite = (method: string | undefined): boolean =>
  !!method && !['get', 'head', 'options'].includes(method.toLowerCase());

/**
 * Makes back/forward show current data after anything has been written.
 *
 * A back or forward navigation re-shows a page from `history.state` without asking the server,
 * so a list left before a rename still shows the old name. With history encryption on, clearing
 * history rotates the key: every earlier entry becomes unreadable, and Inertia fetches the page
 * fresh when the user returns to it. Clearing only after a write keeps ordinary back navigation
 * free, along with its scroll position.
 *
 * API writes go through axios and never produce an Inertia response, so the server's
 * `Inertia::clearHistory()` would not land until the next visit, after the stale page has already
 * been restored. Both paths are cleared on the client instead, the moment the write finishes.
 */
export const installFreshHistoryAfterWrites = (http: AxiosInstance, router: Router): void => {
  http.interceptors.response.use(response => {
    if (isWrite(response.config.method)) {
      router.clearHistory();
    }

    return response;
  });

  router.on('finish', event => {
    const { visit } = event.detail;

    if (visit.completed && isWrite(visit.method)) {
      router.clearHistory();
    }
  });
};
